import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import StaffService from '../services/staffService';
import AuthService from '../services/authService';
import { Staff } from '../types';

const StaffList: React.FC = () => {
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Debounce search function
  const debouncedSearch = useCallback(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (AuthService.isAdmin()) {
        searchStaff();
      }
    }, 500); // Chờ 500ms sau khi người dùng ngừng gõ
    
    setSearchTimeout(timeout);
  }, [searchTerm]);

  // Load staff list on mount and when search term changes
  useEffect(() => {
    // Kiểm tra quyền trước khi tải dữ liệu
    if (!AuthService.isAdmin()) {
      setError('Bạn không có quyền truy cập trang này.');
      setLoading(false);
      return;
    }

    // Nếu không có từ khóa tìm kiếm, load tất cả nhân viên
    if (!searchTerm.trim()) {
      loadStaffList();
    } else {
      // Nếu có từ khóa tìm kiếm, sử dụng debounced search
      debouncedSearch();
    }
    
    // Cleanup function
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, debouncedSearch]);

  const loadStaffList = async () => {
    try {
      setLoading(true);
      const response = await StaffService.getAll();
      setStaffList(response.data);
      setError(null);
    } catch (err) {
      setError('Không thể tải danh sách nhân viên. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchStaff = async () => {
    try {
      setLoading(true);
      // Lọc nhân viên theo tên hoặc username trên client-side vì backend chưa hỗ trợ tìm kiếm
      const response = await StaffService.getAll();
      const filteredStaff = response.data.filter((staff: Staff) => 
        staff.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        staff.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setStaffList(filteredStaff);
      setError(null);
    } catch (err) {
      setError('Không thể tìm kiếm nhân viên. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!AuthService.isAdmin()) {
      setError('Bạn không có quyền xóa nhân viên.');
      return;
    }
    
    if (window.confirm('Bạn có chắc chắn muốn xóa nhân viên này?')) {
      try {
        await StaffService.remove(id);
        loadStaffList();
      } catch (err) {
        setError('Không thể xóa nhân viên. Vui lòng thử lại sau.');
        console.error(err);
      }
    }
  };

  // Nếu người dùng không có quyền admin và đang hiển thị lỗi
  if (error === 'Bạn không có quyền truy cập trang này.') {
    return (
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Danh sách nhân viên</h4>
              </div>
              <div className="card-body">
                <div className="alert alert-danger">
                  {error}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title">Danh sách nhân viên</h4>
                <Link to="/admin/staff/add" className="btn btn-success">
                  <i className="nc-icon nc-simple-add mr-1"></i> Thêm nhân viên mới
                </Link>
              </div>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="search-box">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tìm kiếm theo tên hoặc tên đăng nhập"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {error && error !== 'Bạn không có quyền truy cập trang này.' && <div className="alert alert-danger">{error}</div>}

              {loading ? (
                <div className="text-center spinner-container">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Đang tải...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="text-primary">
                      <tr>
                        <th>ID</th>
                        <th>Tên đăng nhập</th>
                        <th>Họ và tên</th>
                        <th>Vai trò</th>
                        <th>Ngày tạo</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.length > 0 ? (
                        staffList.map((staff) => (
                          <tr key={staff.id}>
                            <td>{staff.id}</td>
                            <td>{staff.username}</td>
                            <td>{staff.fullName || 'N/A'}</td>
                            <td>{staff.role || 'N/A'}</td>
                            <td>
                              {staff.createdAt
                                ? new Date(staff.createdAt).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="table-actions">
                              <Link
                                to={`/admin/staff/${staff.id}`}
                                className="btn btn-info btn-sm me-1"
                              >
                                <i className="nc-icon nc-zoom-split"></i> Xem
                              </Link>
                              <Link
                                to={`/admin/staff/edit/${staff.id}`}
                                className="btn btn-warning btn-sm me-1"
                              >
                                <i className="nc-icon nc-ruler-pencil"></i> Sửa
                              </Link>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => staff.id && handleDelete(staff.id)}
                              >
                                <i className="nc-icon nc-simple-remove"></i> Xóa
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center">
                            Không tìm thấy nhân viên nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffList; 